import { DrawCabinetInsideElement } from "$src/CGS/domain/dto/article/Cabinet/Inside/DrawCabinetInsideElement";
import { DrawInsideShelf } from "$src/CGS/domain/dto/article/Cabinet/Inside/DrawInsideShelf";
import { EInsideElement } from "$src/CGS/domain/dto/core/EInsideElement";
import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { LengthValueMillimeter } from "$src/CGS/domain/dto/core/Length/LengthValueMillimeter";
import RangeLength from "$src/CGS/domain/dto/core/Length/RangeLength";
import { MathUtils, Vector3 } from "three";
import { ApplianceInsideMetadata } from "../ApplianceInsideMetadata";
import { InsideComputation } from "../Compute/InsideComputation";
import { FrontElementMetadata, FrontGroupMetadata, FrontMetadata } from "../FrontMetadata";
import { InsideContainerMetadata } from "../InsideContainerMetadata";
import { FrontFacePlacementMetadata } from "../InsideElementMetadata";
import { LengthValueMeter } from "$src/CGS/domain/dto/core/Length/LengthValueMeter";
import { DrawCabinet } from "$src/CGS/domain/dto/article/Cabinet/DrawCabinet";
import { AutoLength } from "$src/CGS/domain/dto/core/Length/AutoLength";
import { EShelfMaterial } from "$src/CGS/domain/dto/core/EShelfMaterial";
import { EType, FrontFace } from "$src/CGS/domain/dto/core/FrontFace";
import { ShelfInsideElementMetadata } from "../ShelfInsideElementMetadata";
import { Length } from "$src/CGS/domain/dto/core/Length/Length";
import { DrawCabinetInsideGroupElement } from "$src/CGS/domain/dto/article/Cabinet/Inside/DrawCabinetInsideGroupElement";
import { IDrawCabinetInsideElement } from "$src/CGS/domain/dto/article/Cabinet/Inside/IDrawCabinetInsideElement";
import { EOrientation } from "$src/CGS/domain/dto/core/EOrientation";
import { EFrontElement } from "$src/CGS/domain/dto/core/EFrontElement";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";
import { InsideApplianceComputation } from "../Compute/InsideApplianceComputation";
import { InsideGroupComputation } from "../Compute/InsideGroupComputation";
import { ShapeCabinetType, ShapeTypeUtils } from "$src/CGS/domain/dto/core/ShapeTypes/EShapeCabinetType";

export class InsideContainerBuilder {
    private drawCabinet: DrawCabinet;

    constructor(drawCabinet: DrawCabinet) {
        this.drawCabinet = drawCabinet;
    }

    buildSingleInsideContainer(front: FrontMetadata, computation: InsideComputation, parentSurplusHeight: LengthValue, isBottomFront: boolean, isTopFront: boolean): InsideContainerMetadata {
        const insideContainer = new InsideContainerMetadata();
        const insides: DrawInsideShelf[] = [];

        const bottomShelf = this.buildBottomPlank(front, !isBottomFront);

        let diff = LengthMaths.Zero;
        if (bottomShelf !== null) {
            diff = LengthMaths.divideInt(LengthMaths.subtract(this.drawCabinet.Constants.FixedShelfThickness, this.drawCabinet.Constants.GapBetweenFrontElements), 2);
        } else if (isBottomFront) {
            diff = LengthMaths.subtract(this.drawCabinet.Constants.BoxThickness, this.drawCabinet.Constants.GapBottomBox);
        }

        insideContainer.Bottom = new LengthValueMeter(front.Anchor.y +  LengthMaths.getLengthValue(diff).meter);
        insideContainer.VerticalPlankBottom = LengthMaths.subtract(LengthMaths.getLengthValue(insideContainer.Bottom), this.drawCabinet.Constants.FixedShelfThickness);

        if ( LengthMaths.getLengthValue(insideContainer.VerticalPlankBottom).millimeter < 0) {
            insideContainer.VerticalPlankBottom = LengthMaths.Zero;
        }

        if (isTopFront) {
            diff = LengthMaths.subtract(this.drawCabinet.Constants.BoxThickness, this.drawCabinet.Constants.GapTopBox);
        } else {
            diff = LengthMaths.divideInt(LengthMaths.subtract(this.drawCabinet.Constants.FixedShelfThickness, this.drawCabinet.Constants.GapBetweenFrontElements), 2);
        }

        insideContainer.Top = new LengthValueMeter(front.Anchor.y + LengthMaths.getLengthValue(front.Height).meter - LengthMaths.getLengthValue(diff).meter);

        const frontFacesIndex = front.FrontFaces.map(ff => ff.Index);
        const hasAllFaces = this.drawCabinet.Fronts.flatMap(cf => cf.Faces.map(f => f.Index)).every(i => frontFacesIndex.includes(i));

        let startOffset: LengthValue | null = null;
        let endOffset: LengthValue | null = null;
        if (hasAllFaces) {
            const currentFront = this.getNextWithGroup(front, EOrientation.Horizontal);
            if (currentFront.ParentGroup !== null) {
                for (let i = 0; i < currentFront.ParentGroup.Elements.length; i++) {
                    const frontElement = currentFront.ParentGroup.Elements[i];
                    if (this.containsFront(frontElement, front)) {
                        startOffset = new LengthValueMeter(i * LengthMaths.getLengthValue(front.Width).meter);
                        if (i !== currentFront.ParentGroup.Elements.length - 1) {
                            endOffset = new LengthValueMeter((i + 1) * LengthMaths.getLengthValue(front.Width).meter);
                        }
                    }
                }
            }
        }

        insideContainer.StartPlacement = new FrontFacePlacementMetadata(
            front.FrontFaces[0].Index,
            startOffset,
        );

        insideContainer.EndPlacement = new FrontFacePlacementMetadata(
            front.FrontFaces[front.FrontFaces.length - 1].Index,
            endOffset,
        );

        const shelfInsides = this.getInsideElement<DrawInsideShelf>(front, DrawInsideShelf);
        const nbShelf = shelfInsides.length;
        const heightContainer = LengthMaths.getLengthValue(insideContainer.Top).meter - LengthMaths.getLengthValue(insideContainer.Bottom).meter;
        const heightStep = heightContainer / (nbShelf + 1);
        for (let i = 0; i < nbShelf; i++) {
            const shelf = shelfInsides[i];
            const y = LengthMaths.getLengthValue(insideContainer.Bottom).meter + ((i + 1) * heightStep) - LengthMaths.getLengthValue(this.getShelfThickness(shelf)).meter / 2;
            const insideElement = this.buildShelfInsideElement(shelf, new Vector3(0, y, front.Anchor.z));
            insideContainer.Elements.push(insideElement);
        }

        if (bottomShelf !== null) {
            const y = LengthMaths.getLengthValue(insideContainer.Bottom).meter - LengthMaths.getLengthValue(bottomShelf.Height).meter;
            const insideElement = this.buildShelfInsideElement(bottomShelf, new Vector3(0, y, front.Anchor.z));
            insideContainer.Elements.push(insideElement);
        }

        insideContainer.Elements.push(...this.buildAppliances(front, computation, parentSurplusHeight, insideContainer));

        return insideContainer;
    }

    buildAppliances(front: FrontMetadata, computation: InsideComputation, parentSurplusHeight: LengthValue, insideContainer: InsideContainerMetadata): ApplianceInsideMetadata[] {
        const appliances: ApplianceInsideMetadata[] = [];

        const applianceComputations = this.getApplianceComputations(computation);

        const availableSize = Math.min(computation.maxHeight ?? Number.MAX_VALUE, LengthMaths.getLengthValue(front.Height).millimeter);

        const sumMax = applianceComputations.reduce((sum, a) => sum + (a.maxHeight ?? 0), 0);
        const sumMin = applianceComputations.reduce((sum, appliance) => sum + appliance.minHeight, 0);

        const availableSizeForRange = MathUtils.clamp(sumMax - sumMin, 0, availableSize - sumMin);
        const availableSizeForAuto = Math.max(0, availableSize - sumMin - availableSizeForRange);

        const autoCount = applianceComputations.filter(appliance => appliance.maxHeight === null).length;

        const variableSizeMargin = availableSize - sumMin;

        const applianceInsides = applianceComputations.map(c => c.context);
        let applianceHeight = LengthMaths.getLengthValue(insideContainer.Bottom);
        for (const applianceComputation of applianceComputations) {
            const bone = new FrontFacePlacementMetadata(
                front.FrontFaces[0].Index,
                LengthMaths.divide(LengthMaths.getLengthValue(front.Width), 2),
            );

            let height = new LengthValueMillimeter(applianceComputation.minHeight);
            let maximalHeight: LengthValue | null = null;
            const minimumHeight = new LengthValueMillimeter(applianceComputation.minHeight);

            if (applianceComputation.maxHeight === null) {
                height = new LengthValueMillimeter(availableSizeForAuto / autoCount);
                maximalHeight = LengthMaths.add(new LengthValueMillimeter(variableSizeMargin), parentSurplusHeight);
            } else if (applianceComputation.maxHeight > applianceComputation.minHeight) {
                const delta = applianceComputation.maxHeight - applianceComputation.minHeight;
                const reservedSpace = (availableSizeForRange * delta) / (sumMax - sumMin);
                height = new LengthValueMillimeter(applianceComputation.minHeight + reservedSpace);
            }

            const surplusHeight = Math.min(parentSurplusHeight.millimeter + variableSizeMargin, (applianceComputation.maxHeight ?? Number.MAX_VALUE) - height.millimeter);

            const possibleHeight = new RangeLength(minimumHeight, maximalHeight ?? height, undefined, height);

            const applianceMetadata = this.buildApplianceInsideElement(applianceComputation, possibleHeight, new Vector3(front.Anchor.x, applianceHeight.meter, front.Anchor.z), bone);
            appliances.push(applianceMetadata);
            applianceHeight = LengthMaths.add(applianceHeight, height);
        }

        return appliances;
    }

    private createFixedShelf(): DrawInsideShelf {
        const item =  new DrawInsideShelf();

        item.Type = EInsideElement.S;
        item.Depth = new AutoLength();
        item.Width = new AutoLength();
        item.IsFixed = true;
        item.Material = EShelfMaterial.B;
        item.Height = this.drawCabinet.Constants.FixedShelfThickness;

        var shape = this.drawCabinet.CabinetShape;
        var shapeType = shape.Shape;

        item.Face = ShapeCabinetType.Get(shapeType)?.faces.find(f => f.Type === EType.Undefined);
        
        return item;
    }

    private buildShelfInsideElement(drawShelf: DrawInsideShelf, anchor: Vector3): ShelfInsideElementMetadata {
        const item = new ShelfInsideElementMetadata();
        item.Type = drawShelf.Type;
        item.Height = this.getShelfThickness(drawShelf);
        item.Width = new AutoLength();
        item.Depth = new AutoLength();
        item.Context = drawShelf;
        item.FrontPadding =  drawShelf.IsFixed ? undefined : new LengthValueMillimeter(25),
        item.Anchor = anchor;

        return item;
    }

    private buildApplianceInsideElement(applianceComputation: InsideApplianceComputation, height: Length, anchor: Vector3, bone: FrontFacePlacementMetadata): ApplianceInsideMetadata {
        const item =  new ApplianceInsideMetadata();
        item.Type = applianceComputation.context.Type;
        item.Height = height;
        item.Width = applianceComputation.context.Width;
        item.Depth = applianceComputation.context.Depth,
        item.Context = applianceComputation.context;
        item.ApplianceApertureIdentifier = applianceComputation.applianceApertureIdentifier;
        item.Anchor = anchor;
        item.BonePlacement = bone;

        return item;
    }

    private getShelfThickness(drawShelf: DrawInsideShelf): Length {
        if (drawShelf.IsFixed) {
            return this.drawCabinet.Constants.FixedShelfThickness;
        }

        switch (drawShelf.Material) {
            case EShelfMaterial.G:
                return this.drawCabinet.Constants.GlassShelfThickness;
            default:
                return this.drawCabinet.Constants.ShelfThickness;
        }
    }

    private getInsideElement<T extends DrawCabinetInsideElement>(front: FrontMetadata, type: new (...args: any[]) => T): T[] {
        if (front instanceof FrontElementMetadata) {
            return this.getDrawInsides(front?.Context?.Inside ?? null).filter((inside): inside is T => inside instanceof type);
        } else if (front instanceof FrontGroupMetadata) {
            return this.getDrawInsides(front?.ContextGroup?.Inside ?? null).filter((inside): inside is T => inside instanceof type);
        }
        return [];
    }

    private getApplianceComputations(computation: InsideComputation): InsideApplianceComputation[] {
        const insideApplianceTypes: EInsideElement[] = [EInsideElement.A, EInsideElement.DW, EInsideElement.W, EInsideElement.F];
        if (computation instanceof InsideApplianceComputation) {
            return [computation];
        } else if (computation instanceof InsideGroupComputation) {
            return computation.elements.flatMap(this.getApplianceComputations.bind(this));
        }
    
        return [];
    }
    
    private buildBottomPlank(front: FrontMetadata, hasBottomPlank: boolean): DrawInsideShelf | null {
        if (hasBottomPlank && front instanceof FrontElementMetadata) {
            if ([EFrontElement.PO, EFrontElement.DR, EFrontElement.G].includes(front.Type)) {
                hasBottomPlank = false;
            }
        }
    
        if (hasBottomPlank) {
            return this.createFixedShelf();
        }
        return null;
    }
    
    private getNextWithGroup(currentFront: FrontMetadata, orientation: EOrientation): FrontMetadata {
        while (currentFront.ParentGroup !== null && currentFront.ParentGroup.ContextGroup?.Orientation !== orientation) {
            currentFront = currentFront.ParentGroup;
        }
        return currentFront;
    }
    
    private getDrawInsides(drawInside: IDrawCabinetInsideElement | null): IDrawCabinetInsideElement[] {
        const drawInsides: IDrawCabinetInsideElement[] = [];
    
        if (drawInside === null) {
            return drawInsides;
        }
    
        if (drawInside instanceof DrawCabinetInsideGroupElement) {
            for (const inside of drawInside.SubElements) {
                drawInsides.push(...this.getDrawInsides(inside));
            }
        } else if (drawInside instanceof DrawCabinetInsideElement) {
            drawInsides.push(drawInside);
        }
    
        return drawInsides;
    }
    
    private containsFront(f1: FrontMetadata, f2: FrontMetadata): boolean {
        if (f1 === f2) {
            return true;
        }
    
        let isContained = false;
        if (f1 instanceof FrontGroupMetadata) {
            for (const front of f1.Elements) {
                isContained = isContained || this.containsFront(front, f2);
            }
        }
        return isContained;
    }
}    